import { useEffect, useState } from 'react';
import { Clock, Users, Star, ArrowRight, BookOpen, Award, TrendingUp } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoursesPage({ onContactClick }) {
  const [courses, setCourses] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Fallback sample courses
      setCourses([
        {
          id: '1',
          title: 'Full Stack Web Development',
          category: 'web_development',
          description: 'Master React, Node.js, MongoDB, and build production-ready applications.',
          duration: '4 months',
          price: 25000,
          discount_price: 19999,
          level: 'Beginner',
          features: ['Live Projects', 'Job Assistance', 'Certification'],
          featured: true,
        },
        {
          id: '2',
          title: 'AWS Cloud Practitioner',
          category: 'cloud',
          description: 'Learn cloud computing fundamentals and prepare for AWS certification.',
          duration: '2 months',
          price: 15000,
          discount_price: 12999,
          level: 'Beginner',
          features: ['Hands-on Labs', 'Exam Prep', 'AWS Credits'],
          featured: true,
        },
        {
          id: '3',
          title: 'Data Science with Python',
          category: 'data_science',
          description: 'Master Python, Machine Learning, and Data Analysis techniques.',
          duration: '3 months',
          price: 20000,
          discount_price: 16999,
          level: 'Intermediate',
          features: ['Real Datasets', 'Portfolio Projects', 'Industry Tools'],
          featured: true,
        },
        {
          id: '4',
          title: 'Cybersecurity Fundamentals',
          category: 'cybersecurity',
          description: 'Learn ethical hacking, network security, and security operations.',
          duration: '3 months',
          price: 22000,
          discount_price: 18999,
          level: 'Intermediate',
          features: ['Lab Environment', 'CTF Challenges', 'Security Tools'],
          featured: false,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All Courses' },
    { id: 'web_development', label: 'Web Development' },
    { id: 'cloud', label: 'Cloud Computing' },
    { id: 'data_science', label: 'Data Science' },
    { id: 'cybersecurity', label: 'Cybersecurity' },
    { id: 'networking', label: 'Networking' },
  ];

  const filteredCourses = activeCategory === 'all' 
    ? courses 
    : courses.filter(c => c.category === activeCategory);

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-700';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'Advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Breadcrumb Schema for SEO
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.skandaitsolutions.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Courses",
        "item": "https://www.skandaitsolutions.com/courses"
      }
    ]
  };

  // ItemList Schema for course catalog
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "IT Training Courses",
    "description": "Comprehensive IT courses offered by SKANDA IT SOLUTIONS",
    "numberOfItems": courses.length,
    "itemListElement": courses.slice(0, 10).map((course, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Course",
        "name": course.title,
        "description": course.description,
        "provider": {
          "@type": "Organization",
          "name": "SKANDA IT SOLUTIONS",
          "sameAs": "https://www.skandaitsolutions.com"
        }
      }
    }))
  };

  return (
    <div data-testid="courses-page" className="pt-20">
      <SEO 
        title="IT Courses - Web Development, AWS, Data Science Training"
        description="Explore our industry-recognized IT courses including Full Stack Web Development, AWS Cloud Computing, Data Science with Python, Cybersecurity. Get certified with 100% placement assistance at SKANDA IT SOLUTIONS Narasaraopeta."
        keywords="IT courses Narasaraopeta, web development training, AWS certification course, Data Science training, Python course, Full Stack developer course, cloud computing course, coding bootcamp, software development training, job-oriented courses"
        canonicalUrl="https://www.skandaitsolutions.com/courses"
      />
      
      {/* Structured Data for better indexing */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-24">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 mb-6">
            <BookOpen size={18} className="text-blue-400 mr-2" />
            <span className="text-blue-300 text-sm font-medium">Industry-Aligned Curriculum</span>
          </div>
          
          <h1 data-testid="courses-hero-title" className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Transform Your Career
            <span className="block mt-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              With Expert Training
            </span>
          </h1>
          
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-10">
            Industry-recognized courses designed by experts with hands-on projects, real-world applications, and 100% placement assistance.
          </p>
          
          <div className="flex flex-wrap justify-center gap-8 text-white">
            <div className="flex items-center space-x-2">
              <Award className="text-yellow-400" size={24} />
              <span>Certified Programs</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="text-green-400" size={24} />
              <span>Expert Instructors</span>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="text-blue-400" size={24} />
              <span>95% Placement Rate</span>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`category-filter-${cat.id}`}
                className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === cat.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Loading courses...</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No courses found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course, index) => (
                <div
                  key={course.id}
                  data-testid={`course-card-${index}`}
                  className="group bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100 hover:border-blue-200"
                >
                  {/* Course Image/Header */}
                  <div className="relative h-48 bg-gradient-to-br from-blue-600 to-blue-800 overflow-hidden">
                    <div className="absolute inset-0 opacity-30" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
                    }}></div>
                    
                    {course.featured && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center">
                        <Star size={12} className="mr-1" /> Featured
                      </div>
                    )}
                    
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getLevelColor(course.level)}`}>
                        {course.level}
                      </span>
                    </div>
                  </div>

                  {/* Course Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {course.title}
                    </h3>
                    
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                      {course.description}
                    </p>

                    {/* Course Meta */}
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <div className="flex items-center">
                        <Clock size={16} className="mr-1" />
                        {course.duration}
                      </div>
                      <div className="flex items-center">
                        <BookOpen size={16} className="mr-1" />
                        {course.features?.length || 0} Modules
                      </div>
                    </div>

                    {/* Features */}
                    {course.features && course.features.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {course.features.slice(0, 3).map((feature, idx) => (
                          <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Price & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div>
                        {course.price && (
                          <>
                            {course.discount_price && course.discount_price < course.price ? (
                              <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-blue-600">₹{course.discount_price?.toLocaleString()}</span>
                                <span className="text-sm text-slate-400 line-through">₹{course.price?.toLocaleString()}</span>
                              </div>
                            ) : (
                              <span className="text-2xl font-bold text-blue-600">₹{course.price?.toLocaleString()}</span>
                            )}
                          </>
                        )}
                      </div>
                      <button 
                        onClick={onContactClick}
                        data-testid={`enroll-btn-${course.id}`}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all duration-300 group-hover:shadow-lg"
                      >
                        Enroll <ArrowRight size={16} className="ml-2" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Not Sure Which Course to Choose?
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-10">
            Our career counselors are here to help you find the perfect course based on your goals and background.
          </p>
          <button 
            onClick={onContactClick}
            data-testid="courses-cta-button" 
            className="bg-white text-blue-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all duration-300 hover:shadow-2xl"
          >
            Get Free Counseling
          </button>
        </div>
      </section>
    </div>
  );
}
